import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Gift, Inbox, Mail, Phone, Search, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Button, Card, Empty, Field, Modal, NumberInput, PageHead, Segmented, Spinner, useToast } from "../../components/ui";
import { date, initials, relative } from "../../lib/format";
import { useDebounced } from "./Orgs";

const TONE: Record<S["TrialRequestStatus"], "info" | "warn" | "ok" | "danger"> = { new: "info", contacted: "warn", granted: "ok", declined: "danger" };

function Review({ request, onClose }: { request: S["TrialRequest"]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState(request.company);
  const [orgId, setOrgId] = useState<string | null>(request.organization_id ?? null);
  const [credits, setCredits] = useState<number | null>(25);
  const query = useDebounced(q.trim());
  const orgs = useQuery({
    queryKey: ["admin-orgs", query],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs", { params: { query: { q: query || undefined, limit: 8 } } })),
    placeholderData: (prev) => prev,
  });
  const update = useMutation({
    mutationFn: (body: S["TrialRequestUpdate"]) =>
      unwrap(api.PATCH("/v1/admin/trial-requests/{request_id}", { params: { path: { request_id: request.id } }, body })),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["trials"] });
      toast(`Request ${r.status}`);
      onClose();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <Modal title={`Review: ${request.company}`} onClose={onClose} width={640}>
      <div className="stack">
        <div className="grid-2">
          <div className="stack" style={{ gap: 6 }}>
            <b>{request.name}</b>
            <span className="row muted small"><Mail size={14} /> <a href={`mailto:${request.email}`}>{request.email}</a></span>
            {request.phone && <span className="row muted small"><Phone size={14} /> {request.phone}</span>}
          </div>
          <div className="stack small muted" style={{ gap: 6 }}>
            <span>Country: <b>{request.country}</b></span>
            <span>Volume: <b>{request.volume != null ? `${request.volume} designs / month` : "—"}</b></span>
            <span>Received {date(request.created_at, true)}</span>
          </div>
        </div>
        {request.message && <div className="alert">{request.message}</div>}
        <div className="divider" />
        <h3>1 · Link their organisation</h3>
        <p className="small muted">They sign up themselves (self-serve); find their company here, then grant credits.</p>
        <div className="input-icon">
          <Search />
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Company or member email" />
        </div>
        <div className="stack" style={{ gap: 6, maxHeight: 200, overflowY: "auto" }}>
          {orgs.data?.items.map(({ organization: o, balance }) => (
            <button
              key={o.id}
              className="btn"
              style={{ justifyContent: "flex-start", height: "auto", padding: "8px 10px", borderColor: orgId === o.id ? "var(--primary)" : undefined, boxShadow: orgId === o.id ? "0 0 0 3px var(--ring)" : undefined }}
              onClick={() => setOrgId(o.id)}
            >
              <span className="org-avatar" style={{ width: 26, height: 26, fontSize: 11 }}>{initials(o.name)}</span>
              <span style={{ flex: 1, textAlign: "left" }}>{o.name}</span>
              <span className="small faint">{balance} credits</span>
            </button>
          ))}
          {orgs.data && !orgs.data.items.length && <span className="small faint">No organisation matches — they may not have signed up yet.</span>}
        </div>
        <h3>2 · Grant trial credits</h3>
        <Field label="Credits">
          <NumberInput value={credits} min={1} max={100000} step={5} onChange={setCredits} />
        </Field>
        <div className="card-foot" style={{ margin: "4px -20px -20px", justifyContent: "space-between" }}>
          <div className="row">
            <Button variant="danger" icon={<X />} loading={update.isPending} onClick={() => update.mutate({ status: "declined" })}>
              Decline
            </Button>
            <Button loading={update.isPending} onClick={() => update.mutate({ status: "contacted" })} icon={<Mail />}>
              Mark contacted
            </Button>
          </div>
          <Button
            variant="primary"
            icon={<Gift />}
            disabled={!orgId || !credits}
            loading={update.isPending}
            onClick={() => update.mutate({ status: "granted", organization_id: orgId, grant_credits: credits ?? undefined })}
          >
            Grant {credits ?? 0} &amp; close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function AdminTrials() {
  const [status, setStatus] = useState<"all" | S["TrialRequestStatus"]>("new");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<S["TrialRequest"] | null>(null);
  const query = useDebounced(q.trim());
  const trials = useQuery({
    queryKey: ["trials", status, query],
    queryFn: () =>
      unwrap(api.GET("/v1/admin/trial-requests", { params: { query: { status: status === "all" ? undefined : status, q: query || undefined, limit: 100 } } })),
    placeholderData: (prev) => prev,
  });
  return (
    <>
      <PageHead title="Trial requests" description="Requests from the landing page. Review, link their organisation, grant credits — the request closes." />
      <Card pad={false}>
        <div className="toolbar">
          <div className="input-icon">
            <Search />
            <input className="input" placeholder="Name, company or email" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: "new", label: "New" },
              { value: "contacted", label: "Contacted" },
              { value: "granted", label: "Granted" },
              { value: "declined", label: "Declined" },
              { value: "all", label: "All" },
            ]}
          />
        </div>
        {trials.isLoading ? (
          <Spinner />
        ) : trials.error ? (
          <div className="card-body">
            <Alert tone="danger">{errorMessage(trials.error)}</Alert>
          </div>
        ) : !trials.data?.items.length ? (
          <Empty icon={<Inbox />} title={status === "new" ? "Inbox zero" : "Nothing here"}>
            {status === "new" ? "No new trial requests." : "No requests match."}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th className="hide-sm">Contact</th>
                  <th className="num hide-sm">Volume</th>
                  <th>Status</th>
                  <th className="num">Received</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {trials.data.items.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => setOpen(r)}>
                    <td>
                      <div className="row" style={{ flexWrap: "nowrap" }}>
                        <div className="org-avatar">{initials(r.company)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div className="cell-main ellipsis">{r.company}</div>
                          <div className="cell-sub ellipsis">{r.country}{r.message ? ` · “${r.message.slice(0, 60)}${r.message.length > 60 ? "…" : ""}”` : ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="hide-sm">
                      <div className="cell-main">{r.name}</div>
                      <div className="cell-sub">{r.email}</div>
                    </td>
                    <td className="num hide-sm">{r.volume != null ? `${r.volume}/mo` : "—"}</td>
                    <td>
                      <Badge tone={TONE[r.status]}>{r.status}</Badge>
                      {r.organization_id && (
                        <Link to={`/admin/orgs/${r.organization_id}`} onClick={(e) => e.stopPropagation()} style={{ marginLeft: 6 }}>
                          <Building2 size={14} style={{ verticalAlign: -2 }} />
                        </Link>
                      )}
                    </td>
                    <td className="num">{relative(r.created_at)}</td>
                    <td className="right">
                      <Button size="sm">Review</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {open && <Review request={open} onClose={() => setOpen(null)} />}
    </>
  );
}
